import { format } from "date-fns";
import { useContext } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	useGetCustomerAr,
	useGetCustomerCredit,
} from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { UserAvatar } from "~/shared/components/navbar/components/user-avatar/user-avatar.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import styles from "./quote-info-modal.module.css";

type QuoteInfoModalProps = Readonly<{
	onDismiss: () => void;
}>;

type QuoteInfoFieldProps = Readonly<{
	label: string;
	value: string;
}>;

function QuoteInfoField({ label, value }: QuoteInfoFieldProps) {
	const isEmpty = !value;
	return (
		<div className={styles.quoteInfoField}>
			<span className={styles.quoteInfoFieldLabel}>{label}</span>
			<span className={isEmpty ? styles.quoteInfoFieldEmpty : undefined}>
				{isEmpty ? "—" : value}
			</span>
		</div>
	);
}

const formatLocation = (
	addressBookCity: string | null | undefined,
	addressBookState: string | null | undefined,
	addressBookPostalCode: string | null | undefined,
	postalCode: {
		city?: { name: string; state?: { abbreviation: string } } | null;
		postalCode: string;
	},
) =>
	[
		addressBookCity || postalCode.city?.name || "",
		addressBookState || postalCode.city?.state?.abbreviation || "",
		addressBookPostalCode || postalCode.postalCode || "",
	]
		.filter(Boolean)
		.join(" ");

const getShipmentClass = (items: { class: { code: string } }[]): string => {
	if (items.length === 0) {
		return "";
	}
	const uniqueClasses = new Set(items.map((item) => item.class.code));
	if (uniqueClasses.size === 1) {
		const [firstClass] = uniqueClasses;
		return firstClass ?? "";
	}
	return "Multiple Classes";
};

export function QuoteInfoModal({ onDismiss }: QuoteInfoModalProps) {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { isInternal } = useDeploymentType();

	const customerId = quoteState.customer?.id ?? 0;
	const brokerId = quoteState.customer?.primaryBroker?.id ?? 0;
	const customerName = quoteState.customer?.name || "";
	const statusName = quoteState.customer?.status?.name || "";
	const ltlTermsValue = quoteState.customer?.hasSignedLTLAddendum
		? "Yes"
		: "No";
	const primaryBrokerName = quoteState.customer?.primaryBroker?.name || "";

	// AR/credit are internal-only APIs (403 for external), so they're disabled below for external users.
	const { creditInfo } = useGetCustomerCredit(customerId);
	const { arInfo } = useGetCustomerAr(customerId, brokerId, isInternal);

	const arName = arInfo?.name || "None Assigned";
	const availableCredit = creditInfo?.availableCredit ?? 0;

	const shipDate = (() => {
		const parsed = new Date(quoteState.shipmentDate);
		return Number.isNaN(parsed.getTime()) ? "" : format(parsed, "MM/dd/yyyy");
	})();
	const pickupLocation = formatLocation(
		selectedAddressBookItems.pickup?.city,
		selectedAddressBookItems.pickup?.stateProvince,
		selectedAddressBookItems.pickup?.postalCode,
		quoteState.pickupPostalCode,
	);
	const deliveryLocation = formatLocation(
		selectedAddressBookItems.delivery?.city,
		selectedAddressBookItems.delivery?.stateProvince,
		selectedAddressBookItems.delivery?.postalCode,
		quoteState.deliveryPostalCode,
	);
	const totalWeight = quoteState.items.reduce(
		(sum, item) => sum + (item.weight ?? 0),
		0,
	);
	const totalLinearFeet =
		quoteState.customTotalLinearFeet > 0
			? String(quoteState.customTotalLinearFeet)
			: quoteState.totalLinearFeet.toFixed(2);
	const shipmentClass = getShipmentClass(quoteState.items);
	const poNumbers = quoteState.poNumbers ?? "";

	return (
		<TraxModal
			cancelText={"Dismiss"}
			onCancel={onDismiss}
			onClose={onDismiss}
			showConfirmButton={false}
			showConfirmationButtons={true}
			title={"Quote Info"}
			type={"confirmation"}
			width={"95%"}
		>
			<div className={styles.quoteInfoAvatar}>
				<UserAvatar name={customerName || "Customer"} />
			</div>
			<div className={styles.quoteInfoPanel}>
				<QuoteInfoField label={"Customer"} value={customerName} />
				{isInternal ? <QuoteInfoField label={"AR"} value={arName} /> : null}
				{isInternal ? (
					<QuoteInfoField label={"Agreed to LTL Terms"} value={ltlTermsValue} />
				) : null}
				<QuoteInfoField label={"Quote ID"} value={String(quoteState.id)} />
				<QuoteInfoField label={"Status"} value={statusName} />
				{isInternal ? (
					<QuoteInfoField label={"LAE"} value={primaryBrokerName} />
				) : null}
				{isInternal ? (
					// Billing customer isn't tracked separately from the account today, so it mirrors Customer.
					<QuoteInfoField label={"Billing Customer"} value={customerName} />
				) : null}
				{isInternal ? (
					<QuoteInfoField
						label={"Available Credit"}
						value={formatCurrency(availableCredit)}
					/>
				) : null}
				<QuoteInfoField
					label={"Template"}
					value={quoteState.templateName ?? ""}
				/>
				<QuoteInfoField label={"Ship Date"} value={shipDate} />
				<QuoteInfoField label={"Pickup"} value={pickupLocation} />
				<QuoteInfoField label={"Delivery"} value={deliveryLocation} />
				<QuoteInfoField
					label={"Total Weight"}
					value={
						quoteState.items.length > 0
							? `${totalWeight.toLocaleString()} lbs`
							: ""
					}
				/>
				<QuoteInfoField
					label={"Total Linear Feet"}
					value={quoteState.items.length > 0 ? `${totalLinearFeet} ft` : ""}
				/>
				<QuoteInfoField label={"Class"} value={shipmentClass} />
				<QuoteInfoField label={"PO Number(s)"} value={poNumbers} />
				<QuoteInfoField label={"SO Number"} value={quoteState.soNumber} />
				<QuoteInfoField
					label={"Customer Reference"}
					value={quoteState.customerReference}
				/>
			</div>
		</TraxModal>
	);
}
